import React, { useState } from 'react';
import ReactSelect from 'react-select';
import PropTypes from 'prop-types';
import { Controller, useForm } from 'react-hook-form';

import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Typography,
  Switch,
  makeStyles,
  Divider,
  AccordionActions,
  Button,
  TextField
} from '@material-ui/core';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import SettingsIcon from '@material-ui/icons/Settings';
import SendIcon from '@material-ui/icons/Send';
import flespiDeviceService from '../../../../services/FlespiDeviceService';
import toast from 'react-hot-toast';

const useStyles = makeStyles((theme) => ({
  root: {
    width: '100%'
  },
  heading: {
    fontSize: theme.typography.pxToRem(15),
    flexBasis: '33.33%',
    flexShrink: 0,
    color: '#757575',
    fontWeight: '700'
  },
  accordionSummaryDiv: {
    display: 'flex',
    alignItems: 'center'
  },
  accordionDetailsItem: {
    display: 'flex',
    justifyContent: 'space-between',
    marginBottom: '1rem',
    marginTop: '1rem'
  },
  settingsDiv: {
    marginRight: '12px'
  },
  accordionDetails: {
    display: 'flex',
    flexDirection: 'column'
  }
}));

const securityModeOptions = [
  { value: 'pinOnly', label: 'PIN only', id: 0 },
  { value: 'pinAndMac', label: 'PIN and MAC', id: 1 },
  { value: 'onBoth', label: 'MAC only', id: 2 },
  { value: 'none', label: 'None', id: 3 }
];

const modeOptions = [
  { value: 'disable', label: 'Disable', id: 0 },
  { value: 'enableHidden', label: 'Enable hidden', id: 1 },
  { value: 'enableVisible', label: 'Enable visible', id: 2 }
];

const BluetoothWorkMode = ({ currentRecord }) => {
  const classes = useStyles();
  const [expanded, setExpanded] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleChange = (panel) => (event, isExpanded) => {
    setExpanded(isExpanded ? panel : false);
  };

  const {
    control,
    formState: { errors },
    handleSubmit
  } = useForm({
    defaultValues: {
      local_name: currentRecord[0]?.current?.local_name,
      local_pin: currentRecord[0]?.current?.local_pin,
      mode: currentRecord[0]?.current?.mode,
      security_mode: currentRecord[0]?.current?.security_mode
    }
  });

  const handleBluetoothWorkMode = (data) => {
    setLoading(true);

    let numberOfDTCPayload = {
      deviceId: currentRecord[0]?.device_id,
      settSelector: 'bluetooth',
      properties: {
        local_name: data.local_name,
        local_pin: data.local_pin,
        mode: data.mode,
        security_mode: data.security_mode
      }
    };

    flespiDeviceService
      .updateFlespiDeviceSetting(numberOfDTCPayload)
      .then((data) => {
        // eslint-disable-next-line no-console, no-undef
        console.log('data is:', data);
        toast.success(`Successfully updated bluetooth work mode settings`);
      })
      .catch((error) => {
        // eslint-disable-next-line no-console, no-undef
        console.log('error', error?.response?.data?.message);
        toast.error(error?.response?.data?.message || `Unable to update bluetooth work mode settings`);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  // eslint-disable-next-line no-console, no-undef
  console.log('errors', errors);

  return (
    <div style={{ marginBottom: '1rem' }}>
      <form onSubmit={handleSubmit(handleBluetoothWorkMode)}>
        <Accordion expanded={expanded === 'panel1'} onChange={handleChange('panel1')}>
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            aria-controls="panel1bh-content"
            id="panel1bh-header"
            className={classes.accordionSummary}
          >
            <div className={classes.accordionSummaryDiv}>
              <div className={classes.settingsDiv}>
                <SettingsIcon />
              </div>

              <div>
                <Typography className={classes.heading}>Bluetooth work mode</Typography>
              </div>
            </div>
          </AccordionSummary>

          <Divider />

          <AccordionDetails className={classes.accordionDetails}>
            <Typography style={{ color: '#9e9e9e', marginBottom: '10px' }}>Bluetooth work mode</Typography>

            {/* Bluetooth Scenario setting */}
            <div style={{ marginBottom: '0.5rem' }}>
              <Typography style={{ fontSize: '13px', color: '#757575' }}>Bluetooth Scenario setting</Typography>
              <Controller
                control={control}
                name="mode"
                render={({ field }) => (
                  <ReactSelect
                    className="basic-single"
                    classNamePrefix="select"
                    value={modeOptions.find((option) => option.id === field.value)}
                    isSearchable={true}
                    name="mode"
                    options={modeOptions}
                    onChange={(selectedOption) => field.onChange(selectedOption ? selectedOption.id : null)}
                    // eslint-disable-next-line no-undef
                    menuPortalTarget={document.body}
                    styles={{ menuPortal: (base) => ({ ...base, zIndex: 2 }) }}
                  />
                )}
              />
            </div>

            {/* local_name */}
            <div className={classes.accordionDetailsItem}>
              <Controller
                name="local_name"
                control={control}
                render={({ field }) => (
                  <TextField
                    label="Local name"
                    name="local_name"
                    size="small"
                    type="text"
                    fullWidth
                    variant="outlined"
                    {...field}
                    error={!!errors.local_name}
                    helperText={errors.local_name?.message}
                  />
                )}
              />
            </div>

            {/* local_pin */}
            <div className={classes.accordionDetailsItem}>
              <Controller
                name="local_pin"
                control={control}
                render={({ field }) => (
                  <TextField
                    label="Bluetooth pairing PIN code"
                    name="local_pin"
                    size="small"
                    type="number"
                    fullWidth
                    variant="outlined"
                    {...field}
                    error={!!errors.local_pin}
                    helperText={errors.local_pin?.message}
                  />
                )}
              />
            </div>

            {/* security_mode */}
            <div>
              <Typography style={{ fontSize: '13px', color: '#757575' }}>Security mode</Typography>
              <Controller
                control={control}
                name="security_mode"
                render={({ field }) => (
                  <ReactSelect
                    className="basic-single"
                    classNamePrefix="select"
                    value={securityModeOptions.find((option) => option.id === field.value)}
                    isSearchable={true}
                    name="security_mode"
                    options={securityModeOptions}
                    onChange={(selectedOption) => field.onChange(selectedOption ? selectedOption.id : null)}
                    // eslint-disable-next-line no-undef
                    menuPortalTarget={document.body}
                    styles={{ menuPortal: (base) => ({ ...base, zIndex: 2 }) }}
                  />
                )}
              />
            </div>
          </AccordionDetails>

          <AccordionActions style={{ cursor: 'pointer' }}>
            <Button
              size="small"
              color="primary"
              title="Changes will apply to the device"
              type="submit"
              disabled={loading}
            >
              <SendIcon />
            </Button>
          </AccordionActions>
        </Accordion>
      </form>
    </div>
  );
};

export default BluetoothWorkMode;

BluetoothWorkMode.propTypes = {
  currentRecord: PropTypes.array,
  settSelector: PropTypes.string,
  heading: PropTypes.string
};
